<template>
  <div class="video-item" :class="{ 'video-item--dark': dark }">
    <div class="hoverable" @click="click">
      <figure class="video-item-content image is-4by3 is-unselectable">
        <div class="video-item__play-loader">
          <div
            class="spinner is-2by4 is-grey-light is-centered is-normal"
          ></div>
        </div>
        <img class="video-item__thumbnail" :src="thumbnail" />
        <div class="video-item__play is-hidden-touch">
          <icon name="fa-play" />
        </div>
        <div class="video-item__play-shadow is-hidden-touch">
          <icon name="fa-play" />
        </div>
        <div class="video-item__play-shadow-2 is-hidden-touch">
          <icon name="fa-play" />
        </div>
      </figure>
      <p class="video-item__title is-unselectable" @click="click">
        {{ video.title }}
      </p>
    </div>
    <div class="video-item__info is-unselectable">
      <p class="video-item__info__date" v-if="!hideDate">
        <icon class="video-item__info__icon" name="fa-clock" />
        {{ formattedDate }}
      </p>
      <div class="video-item__info__tags" v-if="!hideTags">
        <icon
          class="video-item-tag--category video-item__info__icon"
          name="fa-tags"
        />
        <p
          class="video-item-tag"
          :class="{ 'video-item-tag--clickable': tagClickable }"
          v-for="tag in video.tags"
          :key="tag.id"
          @click="clickTag(tag)"
        >
          {{ tag.name }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { prettyDate } from "%utils/date";

import { Icon } from "%helpers";

export default {
  name: "video-item",

  components: {
    Icon
  },

  props: {
    video: Object,
    dark: Boolean,
    tagClickable: Boolean,
    hideDate: Boolean,
    hideTags: Boolean
  },

  computed: {
    formattedDate() {
      return prettyDate(this.video.createdDate);
    },

    thumbnail() {
      return process.env.VUE_APP_THUMBNAIL_MIN_URL + this.video.thumbnailPath;
    }
  },

  methods: {
    click() {
      this.$emit("click", this.video);
    },

    clickTag(tag) {
      if (this.tagClickable) {
        this.$emit("click-tag", tag);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "bulma/sass/components/card";

.video-item {
  display: inline-block;
  transition: 1.5s;
  vertical-align: top;
  margin: 1%;

  @include mobile {
    width: 96%; /* Single column view. */
    margin: 2%;
  }

  @include tablet {
    width: 48%;
  }

  @include desktop {
    width: 31.333%;
  }

  @include widescreen {
    width: 23%;
  }

  .hoverable {
    cursor: pointer;
  }

  .hoverable:hover > .video-item__title {
    color: $grey;
  }

  .hoverable:hover > .video-item-content > .video-item__play,
  .hoverable:hover > .video-item-content > .video-item__play-shadow,
  .hoverable:hover > .video-item-content > .video-item__play-shadow-2 {
    opacity: 1;
    transition: 0.3s;
  }

  .video-item-content {
    position: relative;
    overflow: hidden;

    .video-item__play,
    .video-item__play-loader,
    .video-item__play-shadow,
    .video-item__play-shadow-2 {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }

    .video-item__play-loader {
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .video-item__play {
      display: flex;
      justify-content: center;
      align-items: center;
      background: transparentize(black, 0.5);
      opacity: 0;
      transition: 0.3s;
      color: $white;
      font-size: 4rem;

      .icon {
        animation: play-pulse 1.5s infinite;
      }
    }

    .video-item__play-shadow {
      display: flex;
      justify-content: center;
      align-items: center;
      opacity: 0;
      transition: 0.3s;
      color: $white;
      font-size: 4rem;

      .icon {
        animation: shadow-pulse 1.5s infinite;
      }
    }

    .video-item__play-shadow-2 {
      display: flex;
      justify-content: center;
      align-items: center;
      opacity: 0;
      transition: 0.3s;
      color: $white;
      font-size: 4rem;

      .icon {
        animation: shadow-2-pulse 1.5s infinite;
      }
    }
  }

  &__title {
    font-family: $family-sans-serif-condensed;
    color: $grey-darker;
    font-weight: 700;
    padding: 0;
    padding-top: 0.2rem;
    font-size: 0.9rem;
  }

  &__info {
    color: $grey-dark;

    &__date {
      font-family: $family-sans-serif-wide;
      font-size: 0.8rem;
      padding: 0;
      padding-bottom: 0.3rem;
    }

    &__icon {
      display: inline-block;
      margin-right: 0.3rem;
    }

    &__tags {
      display: flex;
      flex-wrap: wrap;
    }
  }

  &-tag {
    font-size: 0.7rem;
    font-family: $family-sans-serif-wide;
    display: inline;
    font-weight: 500;

    &--category {
      font-size: 0.7rem;
    }

    &:not(:last-of-type) {
      margin-right: 1rem;
    }

    &--clickable {
      cursor: pointer;

      &:hover {
        color: $grey;
      }
    }
  }
}

@keyframes play-pulse {
  0% {
    transform: scale(1);
  }
  25% {
    transform: scale(1);
  }
  45% {
    transform: scale(1.1);
  }
  55% {
    transform: scale(1);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes shadow-pulse {
  0%,
  50% {
    opacity: 0.3;
    transform: scale(1);
  }
  90%,
  100% {
    transform: scale(3);
    opacity: 0;
  }
}

@keyframes shadow-2-pulse {
  0%,
  70% {
    opacity: 0.2;
    transform: scale(1);
  }
  100% {
    transform: scale(2);
    opacity: 0;
  }
}

.video-item--dark {
  .hoverable:hover > .video-item__title {
    color: $white;
  }

  .video-item__title {
    color: $grey-lighter;
  }

  .video-item__info {
    color: $grey-light;
  }

  .video-item-tag--clickable:hover {
    color: $white;
  }
}
</style>
